import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Avatar,
  Spinner,
  useColorModeValue,
  Input,
  Textarea,
  SimpleGrid,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Switch,
  FormControl,
  FormLabel,
  useToast,
  Tooltip,
  InputGroup,
  InputLeftElement,
  Icon,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import {
  FaUserPlus,
  FaUserMinus,
  FaHeart,
  FaTrash,
  FaLock,
  FaCamera,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaEdit,
} from "react-icons/fa";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import logger from "../utils/logger";
import { colors } from "../assets/styles/colors";

const MotionBox = motion.create(Box);
const MotionHeading = motion.create(Heading);

// Country flags data with language code support
const countryFlags = [
  { code: "US", emoji: "🇺🇸", language: "en" },
  { code: "GB", emoji: "🇬🇧", language: "en" },
  { code: "IL", emoji: "🇮🇱", language: "he" },
  { code: "NL", emoji: "🇳🇱", language: "nl" },
  { code: "FR", emoji: "🇫🇷", language: "fr" },
  { code: "DE", emoji: "🇩🇪", language: "de" },
  { code: "ES", emoji: "🇪🇸", language: "es" },
  { code: "JP", emoji: "🇯🇵", language: "ja" },
  { code: "CN", emoji: "🇨🇳", language: "zh" },
  { code: "BR", emoji: "🇧🇷", language: "pt" },
  { code: "KR", emoji: "🇰🇷", language: "ko" },
  { code: "IT", emoji: "🇮🇹", language: "it" },
  { code: "CA", emoji: "🇨🇦", language: "en" },
  { code: "AU", emoji: "🇦🇺", language: "en" },
  { code: "IN", emoji: "🇮🇳", language: "hi" },
  { code: "RU", emoji: "🇷🇺", language: "ru" },
];

// Language phrases data
const languagePhrases = [
  { language: "English", phrase: "Hello, nice to meet you!", flag: "🇺🇸" },
  { language: "Spanish", phrase: "¡Hola, mucho gusto!", flag: "🇪🇸" },
  { language: "French", phrase: "Bonjour, enchanté!", flag: "🇫🇷" },
  {
    language: "German",
    phrase: "Hallo, schön dich kennenzulernen!",
    flag: "🇩🇪",
  },
  { language: "Japanese", phrase: "こんにちは、はじめまして！", flag: "🇯🇵" },
  { language: "Chinese", phrase: "你好，很高兴认识你！", flag: "🇨🇳" },
  { language: "Hebrew", phrase: "שלום, נעים להכיר!", flag: "🇮🇱" },
  { language: "Russian", phrase: "Привет, приятно познакомиться!", flag: "🇷🇺" },
  { language: "Italian", phrase: "Ciao, piacere di conoscerti!", flag: "🇮🇹" },
  { language: "Portuguese", phrase: "Olá, prazer em conhecê-lo!", flag: "🇧🇷" },
];

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  bio: string;
  country_flag: string;
  favorite_word: string;
  is_public: boolean;
  created_at: string;
  friends: string[];
  avatar_url?: string;
}

interface UserSettings {
  id: string;
  user_id: string;
  dark_mode: boolean;
  target_language: string;
  native_language: string;
  daily_goal: number;
  notifications: boolean;
}

interface UserStats {
  totalWords: number;
  totalSentences: number;
  rememberedSentences: number;
  streak: number;
}

// Animated language phrase component
const AnimatedPhrase = ({
  phrase,
  index,
}: {
  phrase: (typeof languagePhrases)[0];
  index: number;
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  return (
    <MotionBox
      position="absolute"
      top={`${10 + (index % 5) * 16}%`}
      left={isRTL ? undefined : `${(index % 3) * 15}%`}
      right={isRTL ? `${(index % 3) * 15}%` : undefined}
      initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
      animate={{
        opacity: [0, 1, 1, 0],
        x: isRTL
          ? ["0%", "-20%", "-80%", "-100%"]
          : ["0%", "20%", "80%", "100%"],
        transition: {
          duration: 8 + index,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "loop",
          delay: index * 1.5,
        },
      }}
      whileHover={{ scale: 1.1, color: "yellow.400" }}
      color="white"
      fontWeight="medium"
      fontSize={{ base: "sm", md: "md" }}
      textShadow="0 2px 4px rgba(0,0,0,0.5)"
      zIndex={1}
      dir={phrase.language === "Hebrew" ? "rtl" : "ltr"}
    >
      <HStack spacing={2}>
        <Text>{phrase.flag}</Text>
        <Text fontStyle="italic">{phrase.phrase}</Text>
      </HStack>
    </MotionBox>
  );
};

// Floating particles component
const FloatingParticles = () => {
  return (
    <>
      {Array.from({ length: 15 }).map((_, i) => (
        <MotionBox
          key={i}
          position="absolute"
          top={`${Math.random() * 100}%`}
          left={`${Math.random() * 100}%`}
          width={`${Math.random() * 10 + 3}px`}
          height={`${Math.random() * 10 + 3}px`}
          borderRadius="full"
          bg="whiteAlpha.400"
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0.8, 0],
            y: [0, -20],
            transition: {
              duration: 3 + Math.random() * 5,
              repeat: Infinity,
              repeatType: "loop",
              delay: i * 0.3,
            },
          }}
        />
      ))}
    </>
  );
};

const Profile = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isRTL = i18n.dir() === "rtl";

  // State hooks - keep these at the top level and in a consistent order
  const [isEditing, setIsEditing] = useState(false);
  const [friendEmail, setFriendEmail] = useState("");
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [editForm, setEditForm] = useState({
    username: "",
    bio: "",
    country_flag: "",
    favorite_word: "",
    is_public: true,
  });
  const [friendUsernames, setFriendUsernames] = useState<
    Record<string, string>
  >({});
  const [isFollowing, setIsFollowing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Theme variables
  const bgColor = useColorModeValue("white", colors.primary[700]);
  const cardBgColor = useColorModeValue("white", colors.primary[700]);
  const listItemBgColor = useColorModeValue("gray.50", colors.primary[600]);
  const headerBgColor = useColorModeValue("blue.500", colors.primary[500]);
  const gradientStart = useColorModeValue(
    colors.accent.blue,
    colors.accent.blue
  );
  const gradientEnd = useColorModeValue(
    colors.accent.purple,
    colors.accent.purple
  );
  const cardBorderColor = useColorModeValue("gray.200", colors.primary[600]);

  // Modal input specific colors
  const modalInputBgColor = useColorModeValue("gray.50", colors.primary[800]);
  const cancelButtonColor = useColorModeValue("gray.600", "gray.400");
  const cancelButtonHoverBg = useColorModeValue(
    "gray.100",
    colors.primary[600]
  );

  // Compute derived values without hooks
  const isOwnProfile = user && profile && user.id === profile.user_id;

  // Helper functions - these don't use hooks
  const checkIfFollowing = useCallback(
    async (profileId: string) => {
      const userId = user?.id;
      if (!userId) return false;

      try {
        const profile = await apiClient.getProfile();
        return profile?.friends?.includes(profileId) || false;
      } catch (error) {
        logger.error("Error checking friendship status:", error);
        return false;
      }
    },
    [user?.id]
  );

  // Update following status whenever the profile changes
  useEffect(() => {
    if (profile && !isOwnProfile) {
      checkIfFollowing(profile.user_id).then(setIsFollowing);
    }
  }, [profile, isOwnProfile, checkIfFollowing]);

  // Replace the old isFriend function with the state value
  const isFriend = (profileId: string) => isFollowing;

  // Fetch user settings to get target language
  const fetchUserSettings = async (userId: string) => {
    try {
      const data = await apiClient.getUserSettings();

      // If we have data (even if empty array) process it
      if (data && data.settings) {
        setUserSettings(data.settings);

        // Set country flag based on target language if user is editing their own profile
        if (!username || (user && username === profile?.username)) {
          const matchingFlag = countryFlags.find(
            (flag) => flag.language === data.settings.target_language
          );
          if (matchingFlag && !profile?.country_flag) {
            setEditForm((prev) => ({
              ...prev,
              country_flag: matchingFlag.emoji,
            }));
          }
        }
      }
    } catch (error) {
      logger.error("Error processing user settings:", error);
    }
  };

  // Fetch profile data based on username or current user
  const fetchProfileData = async () => {
    setIsProfileLoading(true);
    try {
      let profileData: UserProfile | null = null;

      if (username) {
        // Fetch by username
        profileData = await apiClient.getProfile(username);
      } else if (user) {
        // Fetch current user profile
        profileData = await apiClient.getProfile();
      }

      if (profileData) {
        setProfile(profileData);
        setEditForm({
          username: profileData.username || "",
          bio: profileData.bio || "",
          country_flag: profileData.country_flag || "",
          favorite_word: profileData.favorite_word || "",
          is_public: profileData.is_public,
        });
        await fetchUserStats(profileData.user_id);
        await fetchUserSettings(profileData.user_id);
        if (profileData.friends?.length > 0) {
          await fetchFriendUsernames(profileData.friends);
        }
        if (!isOwnProfile && profileData.user_id) {
          checkIfFollowing(profileData.user_id).then(setIsFollowing);
        }
      } else if (!username && user) {
        logger.warn("Logged in user has no profile entry.");
      }
    } catch (err) {
      logger.error("Error fetching profile data:", err);
      toast({
        title: t("errors.fetchProfileError"),
        status: "error",
        duration: 3000,
      });
      if (username) navigate("/dashboard");
    } finally {
      setIsProfileLoading(false);
    }
  };

  // Fetch user stats
  const fetchUserStats = async (userId: string) => {
    try {
      const statsData = await apiClient.getUserStats(userId);

      setStats({
        totalWords: statsData.totalWords || 0,
        totalSentences: statsData.totalSentences || 0,
        rememberedSentences: statsData.rememberedSentences || 0,
        streak: statsData.streak || 0,
      });
    } catch (error) {
      logger.error("Error fetching stats:", error);
      setStats({
        totalWords: 0,
        totalSentences: 0,
        rememberedSentences: 0,
        streak: 0,
      });
    }
  };

  // Add friend by email
  const handleAddFriend = async () => {
    if (!friendEmail.trim() || !user) return;

    try {
      // First check if the email belongs to the current user
      if (user.email === friendEmail) {
        toast({
          title: t("profile.cannotAddSelf"),
          description: t("profile.cannotAddSelf"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const foundUser = await apiClient.findUserByEmail(friendEmail.trim());

      if (!foundUser) {
        toast({
          title: t("profile.userNotFound"),
          description: t("profile.checkEmail"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Don't allow adding yourself
      if (foundUser.userId === user.id) {
        toast({
          title: t("profile.cannotAddSelf"),
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      addFriendMutation.mutate(foundUser.userId);
      setFriendEmail("");
      onClose();
    } catch (error) {
      logger.error("Error finding user:", error);
      toast({
        title: "Error finding user",
        description: "Could not find a user with that email",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSaveProfile = () => {
    updateProfileMutation.mutate({
      username: editForm.username,
      bio: editForm.bio,
      country_flag: editForm.country_flag,
      favorite_word: editForm.favorite_word,
      is_public: editForm.is_public,
    });
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    if (profile) {
      setEditForm({
        username: profile.username || "",
        bio: profile.bio || "",
        country_flag: profile.country_flag || "",
        favorite_word: profile.favorite_word || "",
        is_public: profile.is_public,
      });
    }
    setIsEditMode(false);
  };

  // Query hooks - always call these even if disabled
  const { data: favoriteSentencesQuery, isLoading: isFavoritesLoading } =
    useQuery({
      queryKey: ["favoriteSentences_v2", profile?.user_id],
      queryFn: async () => {
        if (!profile) return [];
        try {
          return await apiClient.getFavorites();
        } catch (error) {
          logger.error("Error in favorites query:", error);
          return [];
        }
      },
      enabled: !!profile,
    });

  // Mutation hooks - always define these
  const updateProfileMutation = useMutation({
    mutationFn: async (updatedProfile: Partial<UserProfile>) => {
      if (!user) throw new Error("No user logged in");
      await apiClient.updateProfile(updatedProfile);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setIsEditing(false);
      toast({
        title: t("profile.profileUpdated"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchProfileData().then(() => fetchFriendUsernames());
    },
    onError: (error) => {
      logger.error("Error updating profile:", error);
      toast({
        title: t("profile.updateError"),
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const addFriendMutation = useMutation({
    mutationFn: async (friendId: string) => {
      if (!user) throw new Error("No user logged in");

      // Get the current profile with friends array
      const currentProfile = await apiClient.getProfile();

      // Check if already friends
      const currentFriends = currentProfile?.friends || [];
      if (currentFriends.includes(friendId)) {
        throw new Error("Already friends with this user");
      }

      // Add the friend to the array
      const updatedFriends = [...currentFriends, friendId];

      // Update the profile with the new friends array
      await apiClient.updateProfile({ friends: updatedFriends });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      fetchProfileData().then(() => fetchFriendUsernames());
      toast({
        title: t("profile.followSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: t("profile.errorFollowing"),
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const removeFriendMutation = useMutation({
    mutationFn: async (friendId: string) => {
      if (!user) throw new Error("No user logged in");

      const currentProfile = await apiClient.getProfile();
      const currentFriends = currentProfile?.friends || [];
      const updatedFriends = currentFriends.filter((id) => id !== friendId);

      await apiClient.updateProfile({ friends: updatedFriends });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      fetchProfileData();
      toast({
        title: t("profile.unfollowSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: t("profile.errorUnfollowing"),
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: async (sentenceId: number) => {
      await apiClient.toggleFavorite(sentenceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["favoriteSentences_v2", profile?.user_id],
      });
      toast({
        title: "Favorite removed successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: "Error removing favorite",
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Add a function to fetch friend usernames
  const fetchFriendUsernames = async (friendsArray?: string[]) => {
    try {
      // If no friends array is provided, use the current profile's friends
      const friends = friendsArray || profile?.friends;

      // If there are no friends to fetch, return early
      if (!friends || friends.length === 0) {
        return;
      }

      // Get usernames for all friends in the friends array
      const promises = friends.map((id) =>
        apiClient.getUserStats(id).catch(() => null)
      );
      const results = await Promise.all(promises);

      // Create a map of user_id -> username
      const usernameMap: Record<string, string> = {};
      results.forEach((res, index) => {
        if (res && res.profile) {
          usernameMap[friends[index]] = res.profile.username || friends[index];
        }
      });

      setFriendUsernames(usernameMap);
    } catch (error) {
      logger.error("Error processing friend usernames:", error);
    }
  };

  // Fetch profile data on component mount or when username/user changes
  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, user?.id]); // Depend on username and user ID

  // Fetch friend usernames when profile friends change
  useEffect(() => {
    if (profile?.friends && profile.friends.length > 0) {
      fetchFriendUsernames(profile.friends);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.friends]); // Depend only on profile.friends changing

  // --- Image Upload Logic ---
  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      // Disabled for now as we don't have storage on Cloudflare yet
      throw new Error(t("errors.featureNotAvailable") || "Feature not available yet");
    },
    onMutate: () => {
      setIsUploading(true);
    },
    onSuccess: (newAvatarUrl) => {
      toast({ title: t("profile.avatarUpdated"), status: "success" });
      queryClient.invalidateQueries({
        queryKey: ["profile", username || user?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["user-stats", user?.id] });
      if (profile) {
        setProfile({ ...profile, avatar_url: newAvatarUrl });
      }
    },
    onError: (error: Error) => {
      toast({
        title: t("common.error"),
        description: error.message,
        status: "error",
      });
    },
    onSettled: () => {
      setIsUploading(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Basic validation (optional: add size/type checks)
      if (file.size > 5 * 1024 * 1024) {
        // Example: 5MB limit
        toast({
          title: t("errors.fileTooLarge", { maxSize: "5MB" }),
          status: "warning",
        });
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast({ title: t("errors.invalidFileType"), status: "warning" });
        return;
      }

      setSelectedFile(file);
      // Trigger upload immediately after selection
      uploadAvatarMutation.mutate(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  // --- End Image Upload Logic ---

  // Add handleFollowToggle function if it doesn't exist
  const handleFollowToggle = () => {
    if (!user || !profile) return;

    if (isFollowing) {
      removeFriendMutation.mutate(profile.user_id);
    } else {
      addFriendMutation.mutate(profile.user_id);
    }
  };

  // Replace the simple gradient banner with our animated language banner
  const renderLanguageBanner = () => (
    <Box
      h="180px"
      position="relative"
      overflow="hidden"
      bgGradient={`linear(to-r, #1a365d, #3182ce, #6b46c1)`}
    >
      {/* Banner content */}
      <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={0}>
        {/* Dynamic floating particles */}
        <FloatingParticles />
      </Box>

      {/* Animated language phrases */}
      <AnimatePresence>
        {languagePhrases.map((phrase, index) => (
          <AnimatedPhrase key={phrase.language} phrase={phrase} index={index} />
        ))}
      </AnimatePresence>

      {/* Inspirational message */}
      <MotionBox
        position="absolute"
        bottom="15%"
        right={isRTL ? undefined : "5%"}
        left={isRTL ? "5%" : undefined}
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: { delay: 1, duration: 1.5 },
        }}
        textAlign={isRTL ? "left" : "right"}
      >
        <Text
          fontSize={{ base: "lg", md: "xl" }}
          fontWeight="bold"
          color="white"
          textShadow="0 2px 4px rgba(0,0,0,0.6)"
        >
          {t("profile.discoverTheWorld")}
        </Text>
        <Text
          fontSize={{ base: "sm", md: "md" }}
          color="whiteAlpha.900"
          textShadow="0 1px 3px rgba(0,0,0,0.6)"
        >
          {t("profile.oneLanguageAtATime")}
        </Text>
      </MotionBox>
    </Box>
  );

  // Placeholder render functions if they were removed
  const renderStatsSection = () => (
    <Box
      p={6}
      borderRadius="lg"
      bg={cardBgColor}
      borderWidth="1px"
      borderColor={cardBorderColor}
      boxShadow="sm"
      mt={4}
    >
      <Heading size="md" mb={4}>
        {t("profile.statistics")}
      </Heading>
      {stats ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Stat>
            <StatLabel>{t("profile.totalWords")}</StatLabel>
            <StatNumber>{stats.totalWords}</StatNumber>
            <StatHelpText>{t("profile.wordsInVocabulary")}</StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>{t("profile.sentences")}</StatLabel>
            <StatNumber>{stats.totalSentences}</StatNumber>
            <StatHelpText>
              {stats.rememberedSentences} {t("profile.remembered")}
            </StatHelpText>
            {stats.totalSentences > 0 && (
              <Progress
                value={(stats.rememberedSentences / stats.totalSentences) * 100}
                colorScheme="green"
                size="sm"
                mt={2}
                borderRadius="full"
              />
            )}
          </Stat>

          <Stat>
            <StatLabel>{t("profile.streak")}</StatLabel>
            <HStack alignItems="center">
              <StatNumber>{stats.streak}</StatNumber>
              <Text fontSize="xl">🔥</Text>
            </HStack>
            <StatHelpText>{t("profile.daysInARow")}</StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>{t("profile.mastery")}</StatLabel>
            <StatNumber>
              {stats.totalSentences > 0
                ? Math.round(
                    (stats.rememberedSentences / stats.totalSentences) * 100
                  )
                : 0}
              %
            </StatNumber>
            <StatHelpText>{t("profile.masteryRate")}</StatHelpText>
          </Stat>
        </SimpleGrid>
      ) : (
        <Center py={4}>
          <Spinner size="md" color="purple.500" />
        </Center>
      )}
    </Box>
  );
  const renderFriendsSection = () => {
    return (
      <Box
        p={6}
        borderRadius="lg"
        bg={cardBgColor}
        borderWidth="1px"
        borderColor={cardBorderColor}
        boxShadow="sm"
        mt={4}
      >
        <Heading size="md" mb={4}>
          {t("profile.following")}
        </Heading>
        {profile?.friends && profile.friends.length > 0 ? (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {profile.friends.map((friendId) => (
              <Flex
                key={friendId}
                p={3}
                borderRadius="md"
                bg={listItemBgColor}
                align="center"
                justify="space-between"
              >
                <HStack spacing={3}>
                  <Avatar
                    size="sm"
                    name={friendUsernames[friendId] || friendId}
                  />
                  <Text
                    fontWeight="medium"
                    onClick={() =>
                      navigate(`/profile/${friendUsernames[friendId] || ""}`)
                    }
                    cursor="pointer"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {friendUsernames[friendId] || t("profile.unknownUser")}
                  </Text>
                </HStack>
                {isOwnProfile && (
                  <IconButton
                    aria-label={t("common.unfollow")}
                    icon={<FaUserMinus />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => removeFriendMutation.mutate(friendId)}
                  />
                )}
              </Flex>
            ))}
          </SimpleGrid>
        ) : (
          <Text color="gray.500">{t("profile.noFollowing")}</Text>
        )}

        {isOwnProfile && (
          <Button
            leftIcon={<FaUserPlus />}
            colorScheme="purple"
            mt={4}
            onClick={onOpen}
          >
            {t("profile.followUser")}
          </Button>
        )}

        {/* Add friend modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent bg={bgColor}>
            <ModalHeader>{t("profile.followUser")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4}>{t("profile.enterFriendEmail")}</Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaUserPlus} color="gray.500" />
                </InputLeftElement>
                <Input
                  placeholder={t("profile.emailPlaceholder")}
                  value={friendEmail}
                  onChange={(e) => setFriendEmail(e.target.value)}
                  bg={modalInputBgColor}
                />
              </InputGroup>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={onClose}
                color={cancelButtonColor}
                _hover={{ bg: cancelButtonHoverBg }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleAddFriend}
                isDisabled={!friendEmail.trim() || !friendEmail.includes("@")}
              >
                {t("common.follow")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  };

  const renderFavoritesSection = () => {
    return (
      <Box
        p={6}
        borderRadius="lg"
        bg={cardBgColor}
        borderWidth="1px"
        borderColor={cardBorderColor}
        boxShadow="sm"
        mt={4}
      >
        <Heading size="md" mb={4}>
          {t("profile.favorites")}
        </Heading>

        {isFavoritesLoading ? (
          <Center py={4}>
            <Spinner size="md" color="purple.500" />
          </Center>
        ) : favoriteSentencesQuery ? (
          favoriteSentencesQuery.length > 0 ? (
            <VStack spacing={4} align="stretch">
              {favoriteSentencesQuery.map((favorite) => (
                <Box
                  key={favorite.id}
                  p={4}
                  borderRadius="md"
                  bg={listItemBgColor}
                  position="relative"
                >
                  <Text
                    fontWeight="medium"
                    mb={2}
                    dir={i18n.language === "he" ? "rtl" : "ltr"}
                  >
                    {favorite.sentence}
                  </Text>
                  {favorite.translation && (
                    <Text
                      color="gray.500"
                      fontSize="sm"
                      dir={i18n.language === "he" ? "rtl" : "ltr"}
                    >
                      {favorite.translation}
                    </Text>
                  )}
                  {isOwnProfile && (
                    <IconButton
                      aria-label={t("common.remove")}
                      icon={<FaHeart />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      position="absolute"
                      top={2}
                      right={isRTL ? undefined : 2}
                      left={isRTL ? 2 : undefined}
                      onClick={() =>
                        removeFavoriteMutation.mutate(favorite.sentenceId)
                      }
                    />
                  )}
                </Box>
              ))}
            </VStack>
          ) : (
            <Text color="gray.500">{t("profile.noFavorites")}</Text>
          )
        ) : (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertTitle mr={2}>{t("common.error")}</AlertTitle>
            <AlertDescription>{t("errors.loadingFavorites")}</AlertDescription>
          </Alert>
        )}
      </Box>
    );
  };

  const renderBioSection = () => {
    return (
      <Box
        p={6}
        borderRadius="lg"
        bg={cardBgColor}
        borderWidth="1px"
        borderColor={cardBorderColor}
        boxShadow="sm"
        mt={4}
      >
        <Heading size="md" mb={4}>
          {t("profile.bio")}
        </Heading>

        {isEditMode ? (
          <FormControl>
            <Textarea
              value={editForm.bio}
              onChange={(e) =>
                setEditForm({ ...editForm, bio: e.target.value })
              }
              placeholder={t("profile.bioPlaceholder")}
              bg={modalInputBgColor}
              resize="vertical"
              minH="100px"
              dir={i18n.language === "he" ? "rtl" : "ltr"}
            />
          </FormControl>
        ) : profile?.bio ? (
          <Text
            whiteSpace="pre-wrap"
            dir={i18n.language === "he" ? "rtl" : "ltr"}
          >
            {profile.bio}
          </Text>
        ) : (
          <Text color="gray.500" fontStyle="italic">
            {t("profile.noBio")}
          </Text>
        )}
      </Box>
    );
  };

  return (
    <Box maxW="container.xl" mx="auto" px={4} py={8}>
      {isProfileLoading ? (
        <Center py={10}>
          <Spinner size="xl" color="purple.500" />
        </Center>
      ) : !profile ? (
        <Box textAlign="center" py={10}>
          <Icon
            as={FaExclamationTriangle}
            boxSize={12}
            color="yellow.500"
            mb={4}
          />
          <Heading size="lg" mb={4}>
            {t("profile.profileNotFound")}
          </Heading>
          <Text>{t("profile.profileMightNotExist")}</Text>
          <Button
            mt={6}
            colorScheme="purple"
            onClick={() => navigate("/dashboard")}
          >
            {t("common.backToDashboard")}
          </Button>
        </Box>
      ) : (
        <>
          {/* Profile Header */}
          <Box
            position="relative"
            borderRadius="lg"
            overflow="hidden"
            bg={cardBgColor}
            borderWidth="1px"
            borderColor={cardBorderColor}
            boxShadow="sm"
          >
            {/* Replace the Banner/Cover Image with our animated language banner */}
            {renderLanguageBanner()}

            {/* Avatar and Basic Info */}
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              p={6}
              pt={0}
            >
              <HStack
                spacing={6}
                mt={{ base: -12, md: -16 }}
                mb={4}
                align="flex-end"
              >
                <Box position="relative">
                  <Avatar
                    size="xl"
                    name={profile.username}
                    src={profile.avatar_url}
                    border="4px solid white"
                    boxShadow="md"
                  />
                  {isOwnProfile && (
                    <IconButton
                      aria-label={t("profile.changeAvatar")}
                      icon={<FaCamera />}
                      size="sm"
                      colorScheme="purple"
                      isRound
                      position="absolute"
                      bottom="0"
                      right={isRTL ? undefined : "0"}
                      left={isRTL ? "0" : undefined}
                      onClick={triggerFileInput}
                      isLoading={isUploading}
                    />
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                </Box>
                <Box>
                  <HStack>
                    {isEditMode ? (
                      <Input
                        value={editForm.username}
                        onChange={(e) =>
                          setEditForm({ ...editForm, username: e.target.value })
                        }
                        fontSize="2xl"
                        fontWeight="bold"
                        width="auto"
                        bg={modalInputBgColor}
                        dir={i18n.language === "he" ? "rtl" : "ltr"}
                      />
                    ) : (
                      <MotionHeading
                        size="lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        dir={i18n.language === "he" ? "rtl" : "ltr"}
                      >
                        {profile.username}
                      </MotionHeading>
                    )}

                    {isEditMode ? (
                      <Input
                        value={editForm.country_flag}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            country_flag: e.target.value,
                          })
                        }
                        width="80px"
                        fontSize="2xl"
                        bg={modalInputBgColor}
                      />
                    ) : profile.country_flag ? (
                      <Text fontSize="2xl">{profile.country_flag}</Text>
                    ) : null}

                    {isEditMode ? (
                      <FormControl display="flex" alignItems="center" ml={2}>
                        <Switch
                          id="profile-public"
                          isChecked={editForm.is_public}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              is_public: e.target.checked,
                            })
                          }
                          colorScheme="purple"
                          size="sm"
                        />
                        <FormLabel
                          htmlFor="profile-public"
                          mb="0"
                          ml={2}
                          fontSize="sm"
                        >
                          {t("profile.publicProfile")}
                        </FormLabel>
                      </FormControl>
                    ) : !profile.is_public ? (
                      <Tooltip label={t("profile.privateAccount")}>
                        <span>
                          <Icon as={FaLock} color="gray.500" />
                        </span>
                      </Tooltip>
                    ) : null}
                  </HStack>

                  {isEditMode ? (
                    <FormControl mt={3}>
                      <FormLabel fontSize="sm">
                        {t("profile.favoriteWord")}
                      </FormLabel>
                      <Input
                        value={editForm.favorite_word}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            favorite_word: e.target.value,
                          })
                        }
                        placeholder={t("profile.favoriteWordPlaceholder")}
                        bg={modalInputBgColor}
                        size="sm"
                        dir={i18n.language === "he" ? "rtl" : "ltr"}
                      />
                    </FormControl>
                  ) : profile.favorite_word ? (
                    <HStack mt={3} spacing={2}>
                      <Text fontWeight="bold">
                        {t("profile.favoriteWord")}:
                      </Text>
                      <Text dir={i18n.language === "he" ? "rtl" : "ltr"}>
                        {profile.favorite_word}
                      </Text>
                    </HStack>
                  ) : null}
                </Box>
              </HStack>

              {/* Profile Actions */}
              <Flex
                wrap="wrap"
                gap={2}
                align="center"
                justify={{
                  base: "flex-start",
                  md: isRTL ? "flex-start" : "flex-end",
                }}
              >
                {isOwnProfile ? (
                  isEditMode ? (
                    <>
                      <Button
                        colorScheme="purple"
                        leftIcon={<Icon as={FaCheck} />}
                        onClick={handleSaveProfile}
                      >
                        {t("common.save")}
                      </Button>
                      <Button
                        variant="outline"
                        leftIcon={<Icon as={FaTimes} />}
                        onClick={handleCancelEdit}
                      >
                        {t("common.cancel")}
                      </Button>
                    </>
                  ) : (
                    <Button
                      colorScheme="purple"
                      variant="outline"
                      leftIcon={<Icon as={FaEdit} />}
                      onClick={() => setIsEditMode(true)}
                    >
                      {t("profile.editProfile")}
                    </Button>
                  )
                ) : (
                  <Button
                    colorScheme="purple"
                    variant={isFollowing ? "outline" : "solid"}
                    leftIcon={
                      <Icon as={isFollowing ? FaUserMinus : FaUserPlus} />
                    }
                    onClick={handleFollowToggle}
                  >
                    {isFollowing ? t("profile.unfollow") : t("profile.follow")}
                  </Button>
                )}
              </Flex>
            </Flex>
          </Box>

          {/* Profile Content */}
          {renderStatsSection()}
          {renderFriendsSection()}
          {renderFavoritesSection()}
          {renderBioSection()}
        </>
      )}
    </Box>
  );
};

export default Profile;
